import React, { useEffect, useState } from 'react';

import { useMediaQuery, Stack, Typography, useTheme, Box, Paper } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import banner_image from '../../../../assets/banner_image.svg';
import { useTranslation } from 'react-i18next';
import { SCOPE_TYPE } from '../../../../services/credentialFactory';
import { USE_DIRECT_LOGIN } from '../../../../featureFlags';
import SignInButton from '../components/SignInButton';

function SignIn() {
  const theme = useTheme();
  const aboveSmall = useMediaQuery(theme.breakpoints.up('sm'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [isVisible, setIsVisible] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    // Delay the animation slightly to ensure smooth rendering
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Stack
      direction='column'
      alignItems='center'
      sx={{
        minHeight: 'calc(var(--vh) * 100)',
      }}
    >
      <Box
        margin={2}
        marginBottom={0}
        component='img'
        src={banner_image}
        sx={{
          height: aboveSmall ? 36 : isExtraSmall ? 24 : 32,
          filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
          transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
        }}
      />

      <Stack
        flexGrow={1}
        alignItems='center'
        sx={{
          margin: aboveSmall ? '0rem' : '1rem',
          marginTop: aboveSmall ? '6rem' : '3rem',
          maxWidth: '800px',
        }}
      >
        <Box
          sx={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
            transitionDelay: '0.2s',
            width: '100%',
          }}
        >
          <Typography
            variant='h4'
            component='h4'
            fontWeight={600}
            align='center'
            sx={{
              fontSize: isExtraSmall ? '1.75rem' : aboveSmall ? '3rem' : '2rem',
              marginBottom: '1rem',
              background: 'rgba(0, 0, 0, 0.9)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('letsCleanYourInbox')}
          </Typography>

          {!isSmall && (
            <Typography
              variant='subtitle1'
              align='center'
              sx={{
                fontSize: isExtraSmall ? '1rem' : '1.2rem',
                color: 'rgba(0, 0, 0, 0.7)',
                maxWidth: '600px',
                marginBottom: '2rem',
                margin: '0 auto',
              }}
            >
              {t('enterEmail.joinThousands')}
            </Typography>
          )}

          <Paper
            elevation={0}
            sx={{
              padding: isSmall ? '1rem' : '2rem',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              maxWidth: '500px',
              border: '1px solid rgba(66, 133, 244, 0.1)',
              margin: isSmall ? '2rem auto' : '2rem auto',
              justifyItems: 'center',
            }}
          >
            <SignInButton
              useGmailIcon={USE_DIRECT_LOGIN}
              buttonText={t(USE_DIRECT_LOGIN ? 'connectGmail' : 'signInWithGoogle')}
              scopeType={USE_DIRECT_LOGIN ? SCOPE_TYPE.ALL_SCOPES : SCOPE_TYPE.USER_INFO}
            />

            <Stack
              direction='row'
              spacing={6}
              justifyContent='center'
              alignItems='center'
              sx={{ marginTop: '2rem' }}
            >
              <Stack direction='column' spacing={1} alignItems='center'>
                <LockIcon sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '1.4rem' }} />
                <Typography
                  variant='body2'
                  sx={{ color: 'rgba(0, 0, 0, 0.6)', textAlign: 'center' }}
                >
                  {t('enterEmail.secureAndEncrypted')}
                </Typography>
              </Stack>
              <Stack direction='column' spacing={1} alignItems='center'>
                <VerifiedUserIcon sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '1.4rem' }} />
                <Typography
                  variant='body2'
                  sx={{ color: 'rgba(0, 0, 0, 0.6)', textAlign: 'center' }}
                >
                  {t('enterEmail.privacyGuaranteed')}
                </Typography>
              </Stack>
            </Stack>

            <Typography
              variant='body2'
              sx={{
                marginTop: aboveSmall ? '2.5rem' : '2rem',
                fontSize: isExtraSmall ? '0.75rem' : aboveSmall ? '0.85rem' : '0.75rem',
              }}
            >
              {t('signUpAgree')}
              <a className='link' href='https://www.trimbox.io/privacy'>
                {t('privacyPolicy')}
              </a>
            </Typography>
          </Paper>

          <Stack direction='column' spacing={2} alignItems='center' sx={{ marginTop: '2rem' }}>
            <Typography variant='body2' sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
              {t('enterEmail.trustedByTeams')}
            </Typography>
            <Stack
              direction='row'
              spacing={4}
              sx={{
                opacity: 0.5,
                filter: 'grayscale(1)',
              }}
            >
              {/* You'll need to add actual company logos here */}
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                Google
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                Microsoft
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                Apple
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
}

export default SignIn;
